import React, { ReactNode, useEffect, useState } from "react";
import "react-toastify/dist/ReactToastify.css";
import { ApolloProvider } from "@apollo/client";
import {
  Button,
  CheckboxField,
  ItemsPerPage,
  LabelAfter,
  LocalizationProvider,
  Pagination,
  SingleSelectField,
  SingleSelectFieldProps,
  StatsCard,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  TextField,
  Tooltip,
} from "@shoptet/ui";
import { DateTime } from "luxon";
import { Trans, useTranslation } from "react-i18next";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import Select from "react-select";
import Creatable from "react-select/creatable";
import { ToastContainer } from "react-toastify";
import { CancelMetaPageClaimButton } from "./elements/Admin/CancelMetaPageClaimButton";
import { CreditModalIncrease } from "./elements/Admin/ModalCreditIncrease";
import { ResendMetaPageClaimButton } from "./elements/Admin/ResendMetaPageClaimButton";
import { DevThemeSwitcher } from "./elements/DevThemeSwitcher";
import { ErrorBoundary, ErrorFallbackComponent } from "./elements/ErrorBoundary";
import { formatIssueName } from "./elements/GmcIssues";
import { DisabledIcon, Icon } from "./elements/Icons";
import Loading from "./elements/Loading/Loading";
import ScrollToTop from "./elements/ScrollToTop";
import { SmallLoader } from "./elements/SmallLoader";
import TableHeaderArrows from "./elements/TableHeaderArrows";
import {
  AdSystemStateT,
  DaysAgoT,
  GmcIssueSeverityT,
  OrderDirectionT,
  OrganizationStateT,
  OrganizationsOrderT,
  useAdminGmcIssuesQuery,
  useAdminMetaAdsOrganizationsQuery,
  useAdminOrganizationLabelsQuery,
  useAdminOrganizationsQuery,
  useAdminSummaryQuery,
  useSetOrganizationLabelsMutation,
  useSetOrganizationNoteMutation,
} from "./graphql/generated/graphql";
import useDefaultLanguage from "./hooks/useDefaultLanguage";
import { formatDate } from "./i18n/formatDate";
import { cleanUrl, formatFloat, formatMoney, formatPercents } from "./i18n/formatNumbers";
import { useAppSettings } from "./providers/appSettingsProvider";
import { createClient } from "./providers/graphqlProvider";
import { Tab, TabWrapper } from "./ui/Tabs";
import { getUiLocale } from "./utils/getUiLocale";
import { isInIframe } from "./utils/isInIframe";

export const asRichText = <T extends ReactNode>(node: T) => node as T & { __brand: "RichText" };

const AdminNavigation = () => {
  return (
    <TabWrapper>
      <Tab data-test-id="nav-dashboard" to="/" exactMatch>
        Dashboard
      </Tab>
      <Tab data-test-id="nav-organizations" to="/organizations">
        Organizations
      </Tab>
      <Tab data-test-id="nav-gmc-issues" to="/gmc-issues">
        GMC Issues
      </Tab>
      <Tab data-test-id="nav-meta-ads" to="/meta-ads">
        Meta Ads
      </Tab>
    </TabWrapper>
  );
};

type LabelOptionsPropsT = {
  labelsOptions: { label: string; value: string }[];
};

type AdminOrganizationsPagePropsT = {
  labelsExclude: boolean;
  selectedLabels: string[];
};

const Labels = ({ apiToken, labels, labelsOptions }: { apiToken: string; labels: string[] } & LabelOptionsPropsT) => {
  const [setLabels, { loading }] = useSetOrganizationLabelsMutation();

  return (
    <Creatable
      defaultValue={labels.map((label) => ({ value: label, label }))}
      isLoading={loading}
      menuPortalTarget={document.body}
      options={labelsOptions}
      placeholder=""
      isClearable
      isMulti
      onChange={(items) => {
        setLabels({
          context: {
            headers: {
              Authorization: `ApiToken ${apiToken}`,
            },
          },
          variables: { labels: items.map((item) => item.value) },
        });
      }}
    />
  );
};

const OrganizationNote = ({ apiToken = "", note }: { apiToken: string; note: string }) => {
  const [setNote] = useSetOrganizationNoteMutation();
  const [noteContent, setNoteContent] = useState(note || "");
  return (
    <Tooltip closeDelay={300} content={noteContent}>
      <TextField
        label="Poznámka"
        value={noteContent}
        width="md"
        labelHidden
        onChange={setNoteContent}
        onBlur={() => {
          setNote({
            context: {
              headers: {
                Authorization: `ApiToken ${apiToken}`,
              },
            },
            variables: { note: noteContent },
          });
        }}
      />
    </Tooltip>
  );
};

type Filters = {
  adwordsCustomerId: string;
  archived?: boolean;
  gmcId: string;
  labels: string[];
  shoptetId: string;
  shoptetUrl: string;
  startedDaysAgo?: DaysAgoT;
  state?: OrganizationStateT;
};

type LabelOption = {
  label: string;
  value: string;
};

const defaultFilters: Filters = {
  shoptetId: "",
  shoptetUrl: "",
  gmcId: "",
  adwordsCustomerId: "",
  labels: [],
};

const STATE_LABELS = {
  [OrganizationStateT.AccountSuspendedT]: "blokovaná",
  [OrganizationStateT.ActiveT]: "aktivni",
  [OrganizationStateT.NoCreditT]: "není kredit",
  [OrganizationStateT.NoProductsT]: "není produkty",
  [OrganizationStateT.PausedT]: "pozastavená",
  [OrganizationStateT.WizardT]: "wizzard",
};

const stateOptions =
  Object.values(OrganizationStateT).map((value) => ({
    value,
    label: STATE_LABELS[value],
  })) || [];

const STARTED_DAYS_AGO_LABELS = {
  [DaysAgoT.Last_1DayT]: "Polsední 1 den",
  [DaysAgoT.Last_7DaysT]: "Posledních 7 dní",
  [DaysAgoT.Last_30DaysT]: "Posledních 30 dní",
};

const startedDaysAgoOptions =
  Object.values(DaysAgoT).map((value) => ({
    value,
    label: STARTED_DAYS_AGO_LABELS[value],
  })) || [];

const AdminOrganizationsPage = ({ labelsOptions }: LabelOptionsPropsT) => {
  const savedItemsPerPage = parseInt(localStorage.getItem("shoptet_organizations_table_items_per_page") ?? "25");
  const { t: tPagination } = useTranslation("translation", { keyPrefix: "Components.Pagination" });
  const { t: tFilter } = useTranslation("translation", { keyPrefix: "Components.Filter" });
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState<ItemsPerPage>(savedItemsPerPage as ItemsPerPage);
  const [showFilter, setShowFilter] = useState(false);
  const [filterInputShoptetId, setFilterInputShoptetId] = useState("");
  const [filterInputShoptetUrl, setFilterInputShoptetUrl] = useState("");
  const [filterInputGMCId, setFilterInputGMCId] = useState("");
  const [filterInputState, setFilterInputState] = useState<OrganizationStateT | "">("");
  const [filterStartedDaysAgo, setFilterStartedDaysAgo] = useState<DaysAgoT | "">("");
  const [filterAdwordsCustomerId, setFilterAdwordsCustomerId] = useState("");
  const [filterLabels, setFilterLabels] = useState<string[]>([]);
  const [filterArchived, setFilterArchived] = useState<string>("");

  const [orderDirection, setOrderDirection] = useState<OrderDirectionT | "">("");
  const [orderColumn, setOrderColumn] = useState<OrganizationsOrderT | "">("");

  const [filters, setFilters] = useState({ ...defaultFilters });

  const offset = (page - 1) * limit;
  const { adwordsCustomerId, archived, gmcId, labels, shoptetId, shoptetUrl, startedDaysAgo, state } = filters;
  const { data, loading, refetch } = useAdminOrganizationsQuery({
    variables: {
      labels,
      limit,
      offset,
      shoptetId,
      shoptetUrl,
      gmcId,
      adwordsCustomerId,
      ...(state ? { state } : {}),
      ...(startedDaysAgo ? { startedDaysAgo } : {}),
      ...(orderDirection ? { orderDirection } : {}),
      ...(orderColumn ? { orderColumn } : {}),
      ...(archived !== undefined ? { archived } : {}),
    },
  });

  useEffect(() => {
    refetch();
  }, [orderDirection, orderColumn, refetch]);

  if (loading) {
    return (
      <div>
        <h1>Shoptet Campaigns - Admin Organizations</h1>
        <AdminNavigation />
        <SmallLoader />
      </div>
    );
  }

  const totalPages = data?.organizations?.pageInfo?.total ? Math.ceil(data.organizations.pageInfo?.total / limit) : 0;
  const totalItems = data?.organizations?.pageInfo?.total || 0;

  const clearFilters = () => {
    setFilterInputShoptetId("");
    setFilterInputShoptetUrl("");
    setFilterInputGMCId("");
    setFilterStartedDaysAgo("");
    setFilterAdwordsCustomerId("");
    setFilterLabels([]);
    setFilterArchived("");
    setFilters({ ...defaultFilters });
  };

  const toggleFilterButtonHandler = () => {
    setShowFilter(!showFilter);
    clearFilters();
  };

  const applyFilters = () => {
    setFilters({
      shoptetId: filterInputShoptetId,
      shoptetUrl: filterInputShoptetUrl,
      gmcId: filterInputGMCId,
      adwordsCustomerId: filterAdwordsCustomerId,
      labels: filterLabels,
      state: filterInputState === "" ? undefined : filterInputState,
      startedDaysAgo: filterStartedDaysAgo === "" ? undefined : filterStartedDaysAgo,
      archived: filterArchived === "" ? undefined : filterArchived === "true",
    });
    setPage(1);
  };

  const TypedSingleSelectField = SingleSelectField as React.ComponentType<SingleSelectFieldProps>;

  return (
    <div>
      <ScrollToTop />
      <h1>Shoptet Campaigns - Admin Organizations</h1>
      <AdminNavigation />
      <div style={{ margin: "32px 0 24px" }}>
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <Button iconLeft="shp-descending" variant="secondary" onClick={toggleFilterButtonHandler}>
            {tFilter("filter")}
          </Button>
        </div>
        {showFilter && (
          <form
            onSubmit={(event) => {
              event.preventDefault();
              applyFilters();
            }}
          >
            <div style={{ display: "flex", flexWrap: "wrap", gap: "12px" }}>
              <div>
                <LabelAfter label="Shop ID" />
                <TextField
                  label="Shop ID"
                  value={filterInputShoptetId}
                  labelHidden
                  onChange={setFilterInputShoptetId}
                />
              </div>
              <div>
                <LabelAfter label="Doména" />
                <TextField
                  label="Doména"
                  value={filterInputShoptetUrl}
                  labelHidden
                  onChange={setFilterInputShoptetUrl}
                />
              </div>
              <div>
                <LabelAfter label="GMC ID" />
                <TextField label="GMC ID" value={filterInputGMCId} labelHidden onChange={setFilterInputGMCId} />
              </div>
              <div>
                <LabelAfter label="Google Ads ID" />
                <TextField
                  label="Google Ads ID"
                  value={filterAdwordsCustomerId}
                  labelHidden
                  onChange={setFilterAdwordsCustomerId}
                />
              </div>
              <div>
                <LabelAfter label="Status" />
                <TypedSingleSelectField
                  getOptionLabel={(option: LabelOption) => option.label}
                  getOptionValue={(option: LabelOption) => option.value}
                  label=""
                  name="state"
                  options={stateOptions}
                  value={filterInputState}
                  nullable
                  onChange={(value) => setFilterInputState(value as OrganizationStateT)}
                />
              </div>
              <div>
                <LabelAfter label="Datum aktivace" />
                <TypedSingleSelectField
                  getOptionLabel={(option: LabelOption) => option.label}
                  getOptionValue={(option: LabelOption) => option.value}
                  label=""
                  name="startedDaysAgo"
                  options={startedDaysAgoOptions}
                  value={filterStartedDaysAgo}
                  nullable
                  onChange={(value) => setFilterStartedDaysAgo(value as DaysAgoT)}
                />
              </div>
              <div>
                <LabelAfter label="Archived" />
                <TypedSingleSelectField
                  getOptionLabel={(option: LabelOption) => option.label}
                  getOptionValue={(option: LabelOption) => option.value}
                  label=""
                  name="archived"
                  value={filterArchived}
                  options={[
                    { value: "true", label: "Archived" },
                    { value: "false", label: "Not archived" },
                  ]}
                  nullable
                  onChange={(value) => setFilterArchived(value as string)}
                />
              </div>
              <div style={{ width: "220px" }}>
                <LabelAfter label="Labels" />
                <Select<LabelOption, true>
                  defaultValue={filterLabels.map((label) => ({ value: label, label }))}
                  options={labelsOptions}
                  isClearable
                  isMulti
                  onChange={(items) => setFilterLabels(items.map(({ value }) => value))}
                />
              </div>
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "16px", gap: "8px" }}>
              <Button type="submit" variant="action" onClick={applyFilters}>
                {tFilter("to filter")}
              </Button>
              <Button iconLeft="shp-cross" variant="secondary" onClick={clearFilters} />
            </div>
          </form>
        )}
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHeaderCell></TableHeaderCell>
            <TableHeaderCell>Shop ID</TableHeaderCell>
            <TableHeaderCell>GMC status / ID</TableHeaderCell>
            <TableHeaderCell>Google Ads ID</TableHeaderCell>
            <TableHeaderCell>Meta BM ID</TableHeaderCell>
            <TableHeaderCell>Meta AM ID</TableHeaderCell>
            <TableHeaderCell>Doména</TableHeaderCell>
            <TableHeaderCell>Status</TableHeaderCell>
            <TableHeaderCell>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ marginRight: "5px" }}>Datum aktivace</span>
                <TableHeaderArrows
                  arrowBottomClickHandler={() => {
                    setOrderDirection(OrderDirectionT.DescT);
                    setOrderColumn(OrganizationsOrderT.StartedAtT);
                  }}
                  arrowTopClickHandler={() => {
                    setOrderDirection(OrderDirectionT.AscT);
                    setOrderColumn(OrganizationsOrderT.StartedAtT);
                  }}
                />
              </div>
            </TableHeaderCell>
            <TableHeaderCell>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ marginRight: "5px" }}>Měsíční Rozpočet</span>
                <TableHeaderArrows
                  arrowBottomClickHandler={() => {
                    setOrderDirection(OrderDirectionT.DescT);
                    setOrderColumn(OrganizationsOrderT.MonthlyBudgetT);
                  }}
                  arrowTopClickHandler={() => {
                    setOrderDirection(OrderDirectionT.AscT);
                    setOrderColumn(OrganizationsOrderT.MonthlyBudgetT);
                  }}
                />
              </div>
            </TableHeaderCell>
            <TableHeaderCell>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ marginRight: "5px" }}>Kredity</span>
                <TableHeaderArrows
                  arrowBottomClickHandler={() => {
                    setOrderDirection(OrderDirectionT.DescT);
                    setOrderColumn(OrganizationsOrderT.CreditBalanceT);
                  }}
                  arrowTopClickHandler={() => {
                    setOrderDirection(OrderDirectionT.AscT);
                    setOrderColumn(OrganizationsOrderT.CreditBalanceT);
                  }}
                />
              </div>
            </TableHeaderCell>
            <TableHeaderCell>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ marginRight: "5px" }}>Útrata celkem</span>
                <TableHeaderArrows
                  arrowBottomClickHandler={() => {
                    setOrderDirection(OrderDirectionT.DescT);
                    setOrderColumn(OrganizationsOrderT.UsedSpendT);
                  }}
                  arrowTopClickHandler={() => {
                    setOrderDirection(OrderDirectionT.AscT);
                    setOrderColumn(OrganizationsOrderT.UsedSpendT);
                  }}
                />
              </div>
            </TableHeaderCell>
            <TableHeaderCell>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ marginRight: "5px" }}>Zamítnuté produkty</span>
                <TableHeaderArrows
                  arrowBottomClickHandler={() => {
                    setOrderDirection(OrderDirectionT.DescT);
                    setOrderColumn(OrganizationsOrderT.BannedProductsPercentageT);
                  }}
                  arrowTopClickHandler={() => {
                    setOrderDirection(OrderDirectionT.AscT);
                    setOrderColumn(OrganizationsOrderT.BannedProductsPercentageT);
                  }}
                />
              </div>
            </TableHeaderCell>
            <TableHeaderCell>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ marginRight: "5px" }}>Zamítnuté assety</span>
                <TableHeaderArrows
                  arrowBottomClickHandler={() => {
                    setOrderDirection(OrderDirectionT.DescT);
                    setOrderColumn(OrganizationsOrderT.DisapprovedAssetsPercentageT);
                  }}
                  arrowTopClickHandler={() => {
                    setOrderDirection(OrderDirectionT.AscT);
                    setOrderColumn(OrganizationsOrderT.DisapprovedAssetsPercentageT);
                  }}
                />
              </div>
            </TableHeaderCell>
            <TableHeaderCell>Labels</TableHeaderCell>
            <TableHeaderCell>Poznámky</TableHeaderCell>
            <TableHeaderCell>Akce</TableHeaderCell>
          </TableRow>
        </TableHeader>
        <TableBody>
          {data?.organizations?.nodes?.map((organization) => {
            const activeAssetsCount = organization.activeAssetsCount;
            const disapprovedAssetsCount = organization.disapprovedAssetsCount;

            return (
              <TableRow key={organization.id} data-test-id={`organization-row-${organization?.shoptetId}`}>
                <TableCell>
                  {organization?.adminAppPath && (
                    <a href={organization.adminAppPath} target="_blank">
                      <Icon kind="preview-icon" />
                    </a>
                  )}
                </TableCell>
                <TableCell>{organization?.shoptetId}</TableCell>
                <TableCell>
                  <div style={{ textAlign: "center" }}>
                    {organization.gmcAccountSuspended ? <DisabledIcon /> : <Icon kind="disable-property" />}
                  </div>
                  <div style={{ textAlign: "center" }}>
                    {organization.gmcId ? (
                      <a href={`https://merchants.google.com/mc/overview?a=${organization.gmcId}`} target="_blank">
                        {organization.gmcId}
                      </a>
                    ) : (
                      "-"
                    )}
                  </div>
                </TableCell>
                <TableCell>{organization.adwordsCustomerId || "-"}</TableCell>
                <TableCell>
                  {organization.metaAdsSetting?.businessId && (
                    <a
                      href={`https://business.facebook.com/settings/pages?business_id=${organization.metaAdsSetting.businessId}`}
                      target="_blank"
                    >
                      {organization.metaAdsSetting.businessId}
                    </a>
                  )}
                </TableCell>
                <TableCell>
                  {organization.metaAdsSetting?.adAccountId && (
                    <>
                      <div style={{ textAlign: "center" }}>
                        {organization.metaAdsAccountSuspended ? <DisabledIcon /> : <Icon kind="disable-property" />}
                      </div>
                      <div style={{ textAlign: "center" }}>
                        <a
                          href={`https://adsmanager.facebook.com/adsmanager/manage/campaigns?act=${organization.metaAdsSetting.adAccountId}`}
                          target="_blank"
                        >
                          {organization.metaAdsSetting.adAccountId}
                        </a>
                      </div>
                    </>
                  )}
                </TableCell>
                <TableCell>
                  {organization.shoptetUrl && (
                    <a href={organization.shoptetUrl} target="_blank">
                      {cleanUrl(organization.shoptetUrl)}
                    </a>
                  )}
                </TableCell>
                <TableCell>
                  {organization.state ? STATE_LABELS[organization.state] : "-"}
                  {organization.archivedAt && (
                    <span
                      style={{ color: "var(--app-warning)", fontWeight: "bold", fontSize: "0.85em", marginLeft: 8 }}
                    >
                      ARCHIVED
                    </span>
                  )}
                </TableCell>
                <TableCell>
                  <div style={{ textAlign: "right" }}>
                    {organization.state === OrganizationStateT.WizardT || !organization.startedAt
                      ? "-"
                      : formatDate(new Date(organization.startedAt))}
                  </div>
                </TableCell>
                <TableCell>
                  <div data-test-id="cell-monthlyBudget" style={{ textAlign: "center" }}>
                    {organization.monthlyBudget ? formatMoney(organization.monthlyBudget) : "-"}
                  </div>
                </TableCell>
                <TableCell>
                  <div data-test-id="cell-creditBalance" style={{ textAlign: "center" }}>
                    {organization.creditBalance ? formatMoney(organization.creditBalance) : "-"}
                  </div>
                </TableCell>
                <TableCell>
                  <div data-test-id="cell-usedSpend" style={{ textAlign: "right" }}>
                    {organization.usedSpend ? formatMoney(organization.usedSpend) : "-"}
                  </div>
                </TableCell>
                <TableCell>
                  <div style={{ textAlign: "right" }}>{`${organization.allBannedProductsCount}/${
                    organization.productsCount
                  } ${
                    organization.productsCount
                      ? "(" +
                        formatPercents((organization.allBannedProductsCount / organization.productsCount) * 100) +
                        ")"
                      : ""
                  }`}</div>
                </TableCell>
                <TableCell>
                  <div style={{ textAlign: "right", color: "var(--app-critical)" }}>
                    {activeAssetsCount > 0
                      ? `${disapprovedAssetsCount}/${activeAssetsCount} (${formatPercents(
                          (disapprovedAssetsCount / activeAssetsCount) * 100
                        )})`
                      : "-"}
                  </div>
                </TableCell>

                <TableCell>
                  {organization && organization.adminApiToken && (
                    <Labels
                      apiToken={organization.adminApiToken}
                      labels={organization.labels}
                      labelsOptions={labelsOptions}
                    />
                  )}
                </TableCell>
                <TableCell>
                  <OrganizationNote apiToken={organization.adminApiToken || ""} note={organization.note || ""} />
                </TableCell>
                <TableCell>
                  {organization.adminApiToken && <CreditModalIncrease apiToken={organization.adminApiToken} />}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      <Pagination
        jump={
          totalPages > 1 ? (
            <Pagination.Jump
              label={tPagination("show page")}
              totalPages={totalPages}
              getLinkProps={(_page) => ({
                onClick: () => setPage(_page),
                href: `javascript:void(0)`,
              })}
            />
          ) : null
        }
        limits={
          <Pagination.Limits
            itemsPerPage={limit}
            label={tPagination("items per page")}
            onChange={(value) => {
              setLimit(value as ItemsPerPage);
              localStorage.setItem("shoptet_organizations_table_items_per_page", value.toString());
            }}
          />
        }
        pages={
          <Pagination.Pages
            nextPageUrl={totalPages > 1 ? "javascript:void(next)" : null}
            pageCurrent={page}
            pagesTotal={totalPages}
            previousPageUrl={totalPages > 1 ? "javascript:void(prev)" : null}
            getLinkProps={(_page) => ({
              onClick: () => {
                let desiredPage = _page;
                if (_page === "javascript:void(prev)") {
                  desiredPage = page - 1 < 1 ? 1 : page - 1;
                }
                if (_page === "javascript:void(next)") {
                  desiredPage = page + 1 > totalPages ? totalPages : page + 1;
                }
                setPage(desiredPage as number);
              },
              href: `javascript:void(0)`,
            })}
          />
        }
        summary={asRichText(
          <Trans
            components={{ strong: <strong /> }}
            i18nKey="displaying items total"
            t={tPagination}
            values={{ totalItems }}
          />
        )}
      />
    </div>
  );
};

const booleanFilterOptions = [
  { value: "true", label: "Yes" },
  { value: "false", label: "No" },
];

const AdminMetaAdsPage = () => {
  const [page, setPage] = useState(0);
  const [filterShoptetId, setFilterShoptetId] = useState("");
  const [filterShoptetUrl, setFilterShoptetUrl] = useState("");
  const [filterPageClaimRequested, setFilterPageClaimRequested] = useState("");
  const [filterPageClaimApproved, setFilterPageClaimApproved] = useState("");
  const [filterBusinessId, setFilterBusinessId] = useState("");
  const [filterAdAccountId, setFilterAdAccountId] = useState("");
  const [filterPageId, setFilterPageId] = useState("");
  const [filterState, setFilterState] = useState("");
  const [filters, setFilters] = useState<{
    adAccountId?: string;
    businessId?: string;
    pageClaimApproved?: boolean;
    pageClaimRequested?: boolean;
    pageId?: string;
    shoptetId?: string;
    shoptetUrl?: string;
    state?: AdSystemStateT;
  }>({});
  const [orderColumn, setOrderColumn] = useState<string | undefined>(undefined);
  const [orderDirection, setOrderDirection] = useState<OrderDirectionT | undefined>(undefined);
  const TypedSingleSelectField = SingleSelectField as React.ComponentType<SingleSelectFieldProps>;
  const limit = 50;
  const { data, loading } = useAdminMetaAdsOrganizationsQuery({
    variables: {
      limit,
      offset: page * limit,
      shoptetId: filters.shoptetId || undefined,
      shoptetUrl: filters.shoptetUrl || undefined,
      pageClaimRequested: filters.pageClaimRequested,
      pageClaimApproved: filters.pageClaimApproved,
      businessId: filters.businessId || undefined,
      adAccountId: filters.adAccountId || undefined,
      pageId: filters.pageId || undefined,
      state: filters.state,
      orderColumn,
      orderDirection,
    },
  });

  const applyFilters = () => {
    setFilters({
      shoptetId: filterShoptetId.trim(),
      shoptetUrl: filterShoptetUrl.trim(),
      pageClaimRequested: filterPageClaimRequested === "" ? undefined : filterPageClaimRequested === "true",
      pageClaimApproved: filterPageClaimApproved === "" ? undefined : filterPageClaimApproved === "true",
      businessId: filterBusinessId.trim(),
      adAccountId: filterAdAccountId.trim(),
      pageId: filterPageId.trim(),
      state: filterState === "" ? undefined : (filterState as AdSystemStateT),
    });
    setPage(0);
  };

  if (loading) {
    return (
      <div>
        <h1>Shoptet Campaigns - Admin Meta Ads</h1>
        <AdminNavigation />
        <SmallLoader />
      </div>
    );
  }

  const organizations = data?.metaAdsOrganizations?.nodes ?? [];
  const total = data?.metaAdsOrganizations?.pageInfo?.total ?? 0;

  return (
    <div>
      <ScrollToTop />
      <h1>Shoptet Campaigns - Admin Meta Ads</h1>
      <AdminNavigation />
      <form
        style={{ display: "flex", flexWrap: "wrap", gap: "12px", margin: "16px 0" }}
        onSubmit={(event) => {
          event.preventDefault();
          applyFilters();
        }}
      >
        <div>
          <LabelAfter label="Shop ID" />
          <TextField label="Shop ID" value={filterShoptetId} labelHidden onChange={setFilterShoptetId} />
        </div>
        <div>
          <LabelAfter label="Doména" />
          <TextField label="Doména" value={filterShoptetUrl} labelHidden onChange={setFilterShoptetUrl} />
        </div>
        <div>
          <LabelAfter label="Page Claimed" />
          <TypedSingleSelectField
            getOptionLabel={(option: LabelOption) => option.label}
            getOptionValue={(option: LabelOption) => option.value}
            label=""
            name="pageClaimRequested"
            options={booleanFilterOptions}
            value={filterPageClaimRequested}
            nullable
            onChange={(value) => setFilterPageClaimRequested(value as string)}
          />
        </div>
        <div>
          <LabelAfter label="Claim Approved" />
          <TypedSingleSelectField
            getOptionLabel={(option: LabelOption) => option.label}
            getOptionValue={(option: LabelOption) => option.value}
            label=""
            name="pageClaimApproved"
            options={booleanFilterOptions}
            value={filterPageClaimApproved}
            nullable
            onChange={(value) => setFilterPageClaimApproved(value as string)}
          />
        </div>
        <div>
          <LabelAfter label="Business Manager ID" />
          <TextField label="Business Manager ID" value={filterBusinessId} labelHidden onChange={setFilterBusinessId} />
        </div>
        <div>
          <LabelAfter label="Ad Account ID" />
          <TextField label="Ad Account ID" value={filterAdAccountId} labelHidden onChange={setFilterAdAccountId} />
        </div>
        <div>
          <LabelAfter label="Page ID" />
          <TextField label="Page ID" value={filterPageId} labelHidden onChange={setFilterPageId} />
        </div>
        <div>
          <LabelAfter label="State" />
          <TypedSingleSelectField
            getOptionLabel={(option: LabelOption) => option.label}
            getOptionValue={(option: LabelOption) => option.value}
            label=""
            name="state"
            value={filterState}
            options={[
              { label: "Active", value: AdSystemStateT.ActiveT },
              { label: "Paused", value: AdSystemStateT.PausedT },
              { label: "Wizard", value: AdSystemStateT.WizardT },
              { label: "No Credit", value: AdSystemStateT.NoCreditT },
              { label: "No Products", value: AdSystemStateT.NoProductsT },
              { label: "Account Suspended", value: AdSystemStateT.AccountSuspendedT },
            ]}
            nullable
            onChange={(value) => setFilterState(value as string)}
          />
        </div>
        <div style={{ display: "flex", alignItems: "flex-end" }}>
          <Button type="submit" variant="primary">
            Filtrovat
          </Button>
        </div>
      </form>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHeaderCell>Shop ID</TableHeaderCell>
            <TableHeaderCell>Doména</TableHeaderCell>
            <TableHeaderCell>Business Manager</TableHeaderCell>
            <TableHeaderCell>Ad Account</TableHeaderCell>
            <TableHeaderCell>Ad Account Status</TableHeaderCell>
            <TableHeaderCell>Advertiser Verification</TableHeaderCell>
            <TableHeaderCell>Page ID</TableHeaderCell>
            <TableHeaderCell>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ marginRight: "5px" }}>Page Claimed</span>
                <TableHeaderArrows
                  arrowBottomClickHandler={() => {
                    setOrderDirection(OrderDirectionT.DescT);
                    setOrderColumn("page_claim_requested_at");
                  }}
                  arrowTopClickHandler={() => {
                    setOrderDirection(OrderDirectionT.AscT);
                    setOrderColumn("page_claim_requested_at");
                  }}
                />
              </div>
            </TableHeaderCell>
            <TableHeaderCell>Claim Approved</TableHeaderCell>
            <TableHeaderCell>State</TableHeaderCell>
            <TableHeaderCell>Wizard Step</TableHeaderCell>
            <TableHeaderCell>Started At</TableHeaderCell>
          </TableRow>
        </TableHeader>
        <TableBody>
          {organizations.map((org) => {
            const meta = org.metaAdsSetting;
            if (!meta) {
              return null;
            }

            return (
              <TableRow key={org.id}>
                <TableCell>{org.shoptetId}</TableCell>
                <TableCell>
                  {org.shoptetUrl && (
                    <a href={org.shoptetUrl} target="_blank">
                      {cleanUrl(org.shoptetUrl)}
                    </a>
                  )}
                </TableCell>
                <TableCell>
                  {meta.businessId && (
                    <a
                      href={`https://business.facebook.com/settings/pages?business_id=${meta.businessId}`}
                      target="_blank"
                    >
                      {meta.businessId}
                    </a>
                  )}
                </TableCell>
                <TableCell>
                  {meta.adAccountId && (
                    <a
                      href={`https://adsmanager.facebook.com/adsmanager/manage/campaigns?act=${meta.adAccountId}`}
                      target="_blank"
                    >
                      {meta.adAccountId}
                    </a>
                  )}
                </TableCell>
                <TableCell>{meta.adAccountStatus ?? "—"}</TableCell>
                <TableCell>
                  {meta.advertiserVerificationPendingSince ? (
                    <span style={{ color: "var(--app-critical)" }}>
                      Pending since {formatDate(meta.advertiserVerificationPendingSince)}
                    </span>
                  ) : (
                    "—"
                  )}
                </TableCell>
                <TableCell>{meta.pageId ?? "—"}</TableCell>
                <TableCell>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "6px" }}>
                    <div>
                      {meta.pageClaimRequestedAt && !meta.pageClaimFailureReason ? "Yes" : "No"}
                      {meta.pageClaimRequestedAt && (
                        <div style={{ fontSize: "11px", color: "var(--app-text-muted)" }}>
                          {formatDate(meta.pageClaimRequestedAt, {
                            year: "numeric",
                            month: "numeric",
                            day: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                            hour12: false,
                            timeZone: "Europe/Prague",
                          })}
                        </div>
                      )}
                      {meta.pageClaimFailureReason && (
                        <div style={{ fontSize: "11px", color: "var(--app-critical)" }}>
                          {meta.pageClaimFailureReason}
                        </div>
                      )}
                    </div>
                    {meta.pageId && meta.businessId && !meta.pageClaimApproved && org.adminApiToken && (
                      <ResendMetaPageClaimButton apiToken={org.adminApiToken} />
                    )}
                    {meta.pageClaimRequestedAt && !meta.pageClaimApproved && org.adminApiToken && (
                      <CancelMetaPageClaimButton apiToken={org.adminApiToken} />
                    )}
                  </div>
                </TableCell>
                <TableCell>{meta.pageClaimApproved ? "Yes" : "No"}</TableCell>
                <TableCell>{meta.state ?? "—"}</TableCell>
                <TableCell>{meta.wizardStep ?? "—"}</TableCell>
                <TableCell>{meta.startedAt ? formatDate(meta.startedAt) : "—"}</TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      {total > limit && (
        <Pagination
          pages={
            <Pagination.Pages
              nextPageUrl={page + 1 < Math.ceil(total / limit) ? "javascript:void(next)" : null}
              pageCurrent={page + 1}
              pagesTotal={Math.ceil(total / limit)}
              previousPageUrl={page > 0 ? "javascript:void(prev)" : null}
              getLinkProps={(_page) => ({
                onClick: () => {
                  let desiredPage = _page;
                  if (_page === "javascript:void(prev)") {
                    desiredPage = page < 1 ? 1 : page;
                  }
                  if (_page === "javascript:void(next)") {
                    desiredPage = page + 2 > Math.ceil(total / limit) ? Math.ceil(total / limit) : page + 2;
                  }
                  setPage((desiredPage as number) - 1);
                },
                href: "javascript:void(0)",
              })}
            />
          }
        />
      )}
    </div>
  );
};

const AdminGMCIssues = ({}) => {
  const [showNotTranslated, setShowNotTranslatedAll] = useState(false);
  const { t } = useTranslation("translation", { keyPrefix: "Components.GMCIssues" });
  const { data, loading } = useAdminGmcIssuesQuery();
  if (loading) {
    return (
      <div>
        <h1>Shoptet Campaigns - Admin GMC Issues</h1>
        <AdminNavigation />
        <SmallLoader />
      </div>
    );
  }
  return (
    <div>
      <ScrollToTop />
      <h1>Shoptet Campaigns - Admin GMC Issues</h1>
      <AdminNavigation />
      <CheckboxField
        checked={showNotTranslated}
        label="Show issues which are not shown to users"
        value="t"
        onChange={setShowNotTranslatedAll}
      />
      <Table>
        <TableHeader>
          <TableRow>
            <TableHeaderCell>Severity</TableHeaderCell>
            <TableHeaderCell>Translation keys</TableHeaderCell>
            <TableHeaderCell>Issue</TableHeaderCell>
            <TableHeaderCell>Count</TableHeaderCell>
          </TableRow>
        </TableHeader>
        <TableBody>
          {data?.gmcIssues?.map((issue) => {
            const color = issue.severity === GmcIssueSeverityT.ErrorT ? "var(--app-critical)" : "var(--app-warning)";
            const translationKey = `Components.GMCIssues.attribute-message.${issue.name}`;

            const translatedIssueName = formatIssueName(t, issue.name);

            if (translatedIssueName.length > 0 && showNotTranslated) {
              return null;
            }

            return (
              <TableRow key={issue.name} data-test-id={`gmc-issue-row-${issue.name}`}>
                <TableCell>
                  <div style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: "8px" }}>
                    <Icon
                      kind={issue.severity === GmcIssueSeverityT.ErrorT ? "delete-item" : "warning"}
                      className={
                        issue.severity === GmcIssueSeverityT.ErrorT
                          ? "shoptet-icon-color-error"
                          : "shoptet-icon-color-warning"
                      }
                    />
                    <div>
                      <span style={{ color }}>
                        {issue.severity === GmcIssueSeverityT.ErrorT ? t("error issue") : t("warning issue")}
                      </span>
                    </div>
                  </div>
                </TableCell>
                <TableCell>
                  <pre>{translationKey}</pre>
                </TableCell>
                <TableCell>{translatedIssueName}</TableCell>
                <TableCell>{issue.count}</TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
};

const AdminHomePage = ({ labelsExclude, selectedLabels }: AdminOrganizationsPagePropsT) => {
  const { data } = useAdminSummaryQuery({
    variables: {
      labels: selectedLabels,
      exclude: labelsExclude,
      dateFromYesterday: DateTime.utc().minus({ days: 1 }).startOf("day").toISO(),
      dateFromWeek: DateTime.utc().minus({ days: 1 }).startOf("week").toISO(),
      dateFromMonth: DateTime.utc().minus({ days: 1 }).startOf("month").toISO(),
      dateFromYear: DateTime.utc().minus({ days: 1 }).startOf("year").toISO(),
    },
  });
  return (
    <div>
      <ScrollToTop />
      <h1>Shoptet Campaigns - Admin Dashboard</h1>
      <AdminNavigation />
      <h2>Spend</h2>
      <div className="statsCardGrid">
        <StatsCard
          icon="shp-statistics"
          title={"Yesterday's spend"}
          value={data?.adminSummary?.spendYesterday ? formatMoney(data?.adminSummary?.spendYesterday) : "-"}
        />
        <StatsCard
          icon="shp-statistics"
          title={"Week's spend"}
          value={data?.adminSummary?.spendWeek ? formatMoney(data?.adminSummary?.spendWeek) : "-"}
        />
        <StatsCard
          icon="shp-statistics"
          title={"Month's spend"}
          value={data?.adminSummary?.spendMonth ? formatMoney(data?.adminSummary?.spendMonth) : "-"}
        />
        <StatsCard
          icon="shp-statistics"
          title={"Year's spend"}
          value={data?.adminSummary?.spendYear ? formatMoney(data?.adminSummary?.spendYear) : "-"}
        />
        <StatsCard
          icon="shp-statistics"
          title={"All time spend"}
          value={data?.adminSummary?.spendAllTime ? formatMoney(data?.adminSummary?.spendAllTime) : "-"}
        />
      </div>
      <h2>Credit</h2>
      <div className="statsCardGrid">
        <StatsCard
          icon="shp-statistics"
          title={"Yesterday's credit"}
          value={data?.adminSummary?.creditYesterday ? formatMoney(data?.adminSummary?.creditYesterday) : "-"}
        />
        <StatsCard
          icon="shp-statistics"
          title={"Week's credit"}
          value={data?.adminSummary?.creditWeek ? formatMoney(data?.adminSummary?.creditWeek) : "-"}
        />
        <StatsCard
          icon="shp-statistics"
          title={"Month's credit"}
          value={data?.adminSummary?.creditMonth ? formatMoney(data?.adminSummary?.creditMonth) : "-"}
        />
        <StatsCard
          icon="shp-statistics"
          title={"Year's credit"}
          value={data?.adminSummary?.creditYear ? formatMoney(data?.adminSummary?.creditYear) : "-"}
        />
        <StatsCard
          icon="shp-statistics"
          title={"All time credit"}
          value={data?.adminSummary?.creditAllTime ? formatMoney(data?.adminSummary?.creditAllTime) : "-"}
        />
      </div>

      <h2>Spend By tarif (Last Month)</h2>
      {(data?.adminSummary?.statsByTarifMonth || []).map((statsByTarif) => {
        return (
          <div key={statsByTarif.tarifName}>
            <h3>{statsByTarif.tarifName}</h3>
            <div className="statsCardGrid">
              <StatsCard
                icon="shp-statistics"
                title={"Organizations count"}
                value={statsByTarif.organizationsCount ? formatFloat(statsByTarif.organizationsCount) : "-"}
              />
              <StatsCard
                icon="shp-statistics"
                title={"Total Spend"}
                value={statsByTarif.spend ? formatMoney(statsByTarif.spend) : "-"}
              />
              <StatsCard
                icon="shp-statistics"
                title={"Average spend"}
                value={statsByTarif.averageSpend ? formatMoney(statsByTarif.averageSpend) : "-"}
              />
              <StatsCard
                icon="shp-statistics"
                title={"Total Credit"}
                value={statsByTarif.credit ? formatMoney(statsByTarif.credit) : "-"}
              />
              <StatsCard
                icon="shp-statistics"
                title={"Income"}
                value={statsByTarif.income ? formatMoney(statsByTarif.income) : "-"}
              />
            </div>
          </div>
        );
      })}
      <h2>Spend By tarif (All time)</h2>
      {(data?.adminSummary?.statsByTarifAllTime || []).map((statsByTarif) => {
        return (
          <div key={statsByTarif.tarifName}>
            <h3>{statsByTarif.tarifName}</h3>
            <div className="statsCardGrid">
              <StatsCard
                icon="shp-statistics"
                title={"Organizations count"}
                value={statsByTarif.organizationsCount ? formatFloat(statsByTarif.organizationsCount) : "-"}
              />
              <StatsCard
                icon="shp-statistics"
                title={"Total Spend"}
                value={statsByTarif.spend ? formatMoney(statsByTarif.spend) : "-"}
              />
              <StatsCard
                icon="shp-statistics"
                title={"Average spend"}
                value={statsByTarif.averageSpend ? formatMoney(statsByTarif.averageSpend) : "-"}
              />
              <StatsCard
                icon="shp-statistics"
                title={"Total Credit"}
                value={statsByTarif.credit ? formatMoney(statsByTarif.credit) : "-"}
              />
              <StatsCard
                icon="shp-statistics"
                title={"Income"}
                value={statsByTarif.income ? formatMoney(statsByTarif.income) : "-"}
              />
            </div>
          </div>
        );
      })}
      <h2>Global Organizations count</h2>
      <div className="statsCardGrid">
        <StatsCard
          icon="shp-statistics"
          title="all"
          value={data?.adminSummary?.allOrganizations ? formatFloat(data?.adminSummary?.allOrganizations) : "-"}
        />
        <StatsCard
          icon="shp-statistics"
          title="Organizations after wizard"
          value={
            data?.adminSummary?.allAfterWizardOrganizations
              ? formatFloat(data?.adminSummary?.allAfterWizardOrganizations)
              : "-"
          }
        />
        <StatsCard
          icon="shp-statistics"
          title="Active"
          value={data?.adminSummary?.activeOrganizations ? formatFloat(data?.adminSummary?.activeOrganizations) : "-"}
        />
        <StatsCard
          icon="shp-statistics"
          title="Paused"
          value={data?.adminSummary?.pausedOrganizations ? formatFloat(data?.adminSummary?.pausedOrganizations) : "-"}
        />
      </div>
      <h2>Wizard</h2>
      <div className="statsCardGrid">
        <StatsCard
          icon="shp-statistics"
          title="First Page"
          value={
            data?.adminSummary?.firstPageOrganizations ? formatFloat(data?.adminSummary?.firstPageOrganizations) : "-"
          }
        />
        <StatsCard
          icon="shp-statistics"
          title="Suggestion Generation"
          value={
            data?.adminSummary?.suggestionGenerationOrganizations
              ? formatFloat(data?.adminSummary?.suggestionGenerationOrganizations)
              : "-"
          }
        />
        <StatsCard
          icon="shp-statistics"
          title="Image Selection"
          value={
            data?.adminSummary?.imageSelectionOrganizations
              ? formatFloat(data?.adminSummary?.imageSelectionOrganizations)
              : "-"
          }
        />
        <StatsCard
          icon="shp-statistics"
          title="Text Selection"
          value={
            data?.adminSummary?.textSelectionOrganizations
              ? formatFloat(data?.adminSummary?.textSelectionOrganizations)
              : "-"
          }
        />
        <StatsCard
          icon="shp-statistics"
          title="Check"
          value={data?.adminSummary?.checkOrganizations ? formatFloat(data?.adminSummary?.checkOrganizations) : "-"}
        />
        <StatsCard
          icon="shp-statistics"
          title="Finish"
          value={
            data?.adminSummary?.finishWizardOrganizations
              ? formatFloat(data?.adminSummary?.finishWizardOrganizations)
              : "-"
          }
        />
      </div>

      <h2>Income & Spend results</h2>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHeaderCell>Date</TableHeaderCell>
            <TableHeaderCell>
              <div style={{ textAlign: "right" }}>Organizations count</div>
            </TableHeaderCell>
            <TableHeaderCell>
              <div style={{ textAlign: "right" }}>Spend</div>
            </TableHeaderCell>
            <TableHeaderCell>
              <div style={{ textAlign: "right" }}>Credit</div>
            </TableHeaderCell>
            <TableHeaderCell>
              <div style={{ textAlign: "right" }}>Income</div>
            </TableHeaderCell>
          </TableRow>
        </TableHeader>
        <TableBody>
          {(data?.adminSummary?.statsByMonth || []).map((statByMonth) => (
            <TableRow key={statByMonth.date}>
              <TableCell>{statByMonth.date}</TableCell>
              <TableCell>
                <div style={{ textAlign: "right" }}>{formatFloat(statByMonth.organizationsCount)}</div>
              </TableCell>
              <TableCell>
                <div style={{ textAlign: "right" }}>{formatMoney(statByMonth.spend)}</div>
              </TableCell>
              <TableCell>
                <div style={{ textAlign: "right" }}>{formatMoney(statByMonth.credit)}</div>
              </TableCell>
              <TableCell>
                <div style={{ textAlign: "right" }}>{formatMoney(statByMonth.income)}</div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

export type AuthorizationGuardPropsT = {
  currentUrl: string;
};

const AuthorizationGuard = ({ currentUrl }: AuthorizationGuardPropsT) => {
  const [selectedLabels, setSelectedLabels] = useState<string[]>([]);
  const [labelsExclude, setLabelsExclude] = useState(true);
  const { data, error, loading } = useAdminOrganizationLabelsQuery({
    fetchPolicy: "no-cache",
  });

  if (error) {
    return <ErrorFallbackComponent />;
  }
  if (loading) {
    return <Loading />;
  }

  const labelsOptions: LabelOption[] =
    data?.organizationLabels?.map((label) => ({
      value: label.name,
      label: label.name,
    })) || [];

  return (
    <div>
      <div
        style={{
          padding: "30px",
          zIndex: 100,
          position: "relative",
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          gap: "10px",
        }}
      >
        <h2 style={{ margin: 0 }}>Filter</h2>
        <Button disabled={!labelsExclude} variant="secondary" onClick={() => setLabelsExclude(false)}>
          {!labelsExclude ? "Included" : "Include"}
        </Button>
        <Button disabled={labelsExclude} variant="secondary" onClick={() => setLabelsExclude(true)}>
          {labelsExclude ? "Excluded" : "Exclude"}
        </Button>

        <div style={{ flexGrow: 1 }}>
          <Select<LabelOption, true>
            defaultValue={selectedLabels.map((label) => ({ value: label, label }))}
            options={labelsOptions}
            placeholder="Filter labels"
            isClearable
            isMulti
            onChange={(items) => setSelectedLabels(items.map(({ value }) => value))}
          />
        </div>
      </div>
      <ScrollToTop />
      <Routes>
        <Route path={currentUrl}>
          <Route element={<AdminHomePage labelsExclude={labelsExclude} selectedLabels={selectedLabels} />} index />
          <Route element={<AdminGMCIssues />} path="/gmc-issues" />
          <Route element={<AdminMetaAdsPage />} path="/meta-ads" />
          <Route element={<AdminOrganizationsPage labelsOptions={labelsOptions} />} path="/organizations" />
        </Route>
      </Routes>
    </div>
  );
};

function Admin() {
  const { apiToken, apiUri, currentUrl, defaultLocale, urlLocale } = useAppSettings();
  useDefaultLanguage({ defaultLocale, urlLocale });

  return (
    <div style={{ padding: isInIframe() ? 0 : "40px" }}>
      <ErrorBoundary>
        <LocalizationProvider locale={getUiLocale(urlLocale || defaultLocale)}>
          <BrowserRouter basename="/admin">
            <ApolloProvider client={createClient({ apiUri, apiToken })}>
              <AuthorizationGuard currentUrl={currentUrl} />
              <ToastContainer />
              <DevThemeSwitcher />
            </ApolloProvider>
          </BrowserRouter>
        </LocalizationProvider>
      </ErrorBoundary>
    </div>
  );
}

export default Admin;
